import { useCallback, useEffect, useMemo, useState } from 'react'

import { FilterQuery, YesObject } from '@yes.technology/react-toolkit'

import { debounce } from 'lodash'
import { useObjectclass } from 'objectclass/shared'
import { useTranslation } from 'react-i18n-lite'
import { useSearchParams } from 'react-router-dom'
import { useBreakpoint } from 'shared/hooks/useBreakpoint'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { useSiteState } from 'siteState/shared'
import { useFetchYesObjects } from 'yesObject/shared'
import useClassification from '../DataCards/hooks/useClassification'
import { KanbanMainContainer, SIZE_MAP } from './Kanban.styles'
import {
  AssociatedUsersRelationsProps,
  ColumnProps,
  KanbanCardsProps
} from './Kanban.types'
import KanbanAreaLine from './components/KanbanAreaLine'
import KanbanAutoColumns from './components/KanbanAutoColumns'
import KanbanSkeleton from './components/KanbanSkeleton'
import {
  filterSectionsByLayout,
  getNewTaskColumnIndex,
  updateTaskColumn
} from './helpers'
import useRelationObjects from './hooks/useRelationObjects'
import useTasksByProcess from './hooks/useTasksByProcess'
import useMergedFilterQuery from 'site/shared/hooks/useMergedFilterQuery'

const DEFAULT_IMAGE_PATH =
  'establishment_organization.person_establishment.profile_picture.url'

const EMPTY_OBJECTS = [] as YesObject[]

function Kanban({ props }: KanbanCardsProps) {
  const {
    uuid_objectclass: siteModelObjectclassUuid,
    uuid_statusaction_classification: statusActionClassificationUuid,
    card_model: cardModel,
    group_by: groupByField,
    return_on_finished_interaction: returnOnFinishedInteraction,
    associated_users_relations: associatedRelations,
    layout,
    styleMap,
    objects = EMPTY_OBJECTS,
    visualizeUrl = '',
    orders_to_use,
    polling_interval
  } = props

  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const processUuid = searchParams.get('process-uuid') ?? ''
  const taskmodelUuid = searchParams.get('taskmodel-uuid') ?? ''

  const { tasks } = useTasksByProcess({ processUuid, taskmodelUuid })

  const styleProps = useStyleMaps(styleMap)

  const { classification: statusActionClassification } = useClassification({
    classificationUuid: statusActionClassificationUuid
  })

  const objectclassUuid = siteModelObjectclassUuid

  const { objectclass, isLoading: isLoadingObjectClass } = useObjectclass({
    objectclassUuid
  })

  const [filterQuery] = useSiteState<FilterQuery>('filter-query')

  const taskQuery: FilterQuery = useMemo(
    () =>
      tasks
        ? ({
            $or: tasks.map((item) => ({ uuid: { $eq: item.uuid } }))
          } as FilterQuery)
        : {},
    [tasks]
  )

  const query = useMergedFilterQuery({
    filterQuery,
    manifestFilterQuery: taskQuery
  })

  const { yesObjects, isLoading: isLoadingObjects } = useFetchYesObjects({
    initialSortOptions: layout?.order_by,
    initialPagination: {},
    filterBody: {
      query: query as FilterQuery,
      objectclass: objectclassUuid,
      skip_relations: false,
      skip_metadata: true
    },
    shouldMergeFilters: true,
    enabled: (!!filterQuery || !!tasks) && !!objectclassUuid,
    refetchInterval: polling_interval
  })

  const [mutableYesObjects, setMutableYesObjects] = useState<YesObject[]>([])

  const { relationObjects, isLoading: isLoadingFieldValues } =
    useRelationObjects({
      field_column: groupByField,
      objectclass: objectclass,
      objectclass_uuid: objectclassUuid
    })

  const breakpoint = useBreakpoint()
  const pageColumns = SIZE_MAP[cardModel][breakpoint]
  const [currentSection, setCurrentSection] = useState(0)

  const isLoading =
    isLoadingObjects || isLoadingObjectClass || isLoadingFieldValues

  useEffect(() => {
    // !!!: Breakpoint só pra evitar bugs com a mudança do tamanho
    setCurrentSection(0)
  }, [breakpoint, relationObjects])

  useEffect(() => {
    if (!!filterQuery && !!objectclassUuid) {
      setMutableYesObjects(yesObjects)
    }
  }, [filterQuery, objectclassUuid, yesObjects])

  useEffect(() => {
    if (!!objects && objects.length > 0) {
      setMutableYesObjects(objects)
    }
  }, [objects])

  useEffect(() => {
    if (tasks && tasks?.length > 0) {
      setMutableYesObjects(yesObjects)
    }
  }, [tasks, yesObjects])

  const groupedYesObjects = relationObjects.reduce(
    (acc, relationObject) => {
      return {
        ...acc,
        [relationObject.code as string]: mutableYesObjects.filter(
          (yesObject) => yesObject[groupByField] === relationObject.code
        )
      }
    },
    {} as Record<string, YesObject[]>
  )

  const orderedColumnsAcrossSections = layout?.sections?.reduce(
    (acc: string[], section) => {
      section.columns.forEach((column) => acc.push(column.grouped_by_value))

      return acc
    },
    []
  )

  const { filteredSections, columnsOverrideAttributes } =
    filterSectionsByLayout(groupedYesObjects, layout?.sections)

  const orderedSections =
    orderedColumnsAcrossSections || Object.keys(filteredSections)

  const totalSections = Object.keys(filteredSections).length

  const onMoveTask = useCallback(
    (
      taskUuid: string,
      newStatusColumn: string,
      setIsFadeInAnimationStarted: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      const newTaskColumnIndex = getNewTaskColumnIndex({
        filteredSections,
        newStatusColumn,
        pageColumns
      })

      if (newTaskColumnIndex >= 0) {
        setCurrentSection(newTaskColumnIndex)
      }

      setMutableYesObjects((prev) =>
        updateTaskColumn({ objects: prev, newStatusColumn, taskUuid })
      )

      setIsFadeInAnimationStarted(true)
      debounce(() => {
        setIsFadeInAnimationStarted(false)
      }, 2000)()
    },
    [filteredSections, pageColumns]
  )

  const totalNavigationPages = Math.ceil(totalSections / pageColumns)
  const shouldDisplayNavigation = !isLoadingObjects && totalNavigationPages > 0
  const { column_labels_text_fading: columnLabelsTextFading = true } =
    layout || {}

  const associatedUsersRelations = useMemo(() => {
    const DEFAULT_ASSOCIATED_USERS_RELATIONS: AssociatedUsersRelationsProps = [
      {
        relation: 'organization_executor',
        readableName: t('task.assigned-executor')
      },
      {
        relation: 'organization_requester',
        readableName: t('task.assigned-requester')
      }
    ]

    return [
      ...(Array.isArray(associatedRelations) && associatedRelations.length > 0
        ? associatedRelations
        : DEFAULT_ASSOCIATED_USERS_RELATIONS)
    ].reduce((acc: AssociatedUsersRelationsProps, association) => {
      const currentAssociationFoundOnDefaultFields =
        DEFAULT_ASSOCIATED_USERS_RELATIONS.find(
          ({ relation }) => relation === association.relation
        )
      let associationReadableName =
        association.readableName ??
        objectclass?.fields?.[association.relation]?.display_text?.des

      if (currentAssociationFoundOnDefaultFields) {
        associationReadableName =
          currentAssociationFoundOnDefaultFields.readableName
      }

      acc.push({
        ...association,
        image_path: association.image_path || DEFAULT_IMAGE_PATH,
        readableName: associationReadableName
      })

      return acc
    }, [])
  }, [associatedRelations, objectclass?.fields, t])

  return (
    <KanbanMainContainer style={styleProps}>
      <KanbanAreaLine
        objectclassName={objectclass?.des}
        currentSection={currentSection}
        totalNavigationPages={totalNavigationPages}
        setCurrentSection={setCurrentSection}
        shouldDisplayNavigation={shouldDisplayNavigation}
      >
        {isLoading && <KanbanSkeleton pageColumns={pageColumns} />}

        {!isLoading && relationObjects.length > 0 && groupedYesObjects && (
          <KanbanAutoColumns
            cardModel={cardModel}
            associatedUsersRelations={associatedUsersRelations}
            returnOnFinishedInteraction={returnOnFinishedInteraction}
            relationObjects={relationObjects}
            groupedYesObjects={groupedYesObjects}
            currentSection={currentSection}
            columnsPerPage={pageColumns}
            orderedSections={orderedSections}
            columnLabelsTextFading={columnLabelsTextFading}
            columnsOverrideAttributes={columnsOverrideAttributes}
            statusActionClassification={statusActionClassification}
            visualizeUrl={visualizeUrl}
            onMoveTask={onMoveTask}
            ordersToUse={orders_to_use}
          />
        )}
      </KanbanAreaLine>
    </KanbanMainContainer>
  )
}

export default Kanban
